import { useEffect } from "preact/hooks";
import type { App } from "../app.ts";
import { useAppState } from "./hooks.ts";

/** The headings of the note as a list; the one the reader is in is marked. */
export function OutlineList({ app }: { app: App }) {
  const s = useAppState(app);
  const top = Math.min(...s.headings.map((h) => h.level));
  return (
    <ol class="outline-list">
      {s.headings.map((h, i) => (
        <li key={`${h.line}:${h.text}`} style={{ "--depth": h.level - top }}>
          <button
            type="button"
            class="outline-item"
            aria-current={i === s.currentHeading ? "location" : undefined}
            onClick={() => app.goToHeading(i)}
          >
            <span class="outline-text">{h.text}</span>
            <span class="outline-tick" aria-hidden="true" />
          </button>
        </li>
      ))}
    </ol>
  );
}

/** Whether there is anything to list, and an editor able to jump there. */
export function useHasOutline(app: App): boolean {
  const s = useAppState(app);
  return s.outlineReady && s.headings.length > 0 && Boolean(s.current);
}

/** Outline as slim ticks at the page edge that open into the list on hover or focus. */
export function OutlineRail({ app }: { app: App }) {
  const has = useHasOutline(app);
  return (
    <nav class="outline-rail" aria-label="Outline" hidden={!has}>
      <OutlineList app={app} />
    </nav>
  );
}

/** Keeps "where am I" up to date as the page scrolls. */
export function useOutlineTracking(app: App): void {
  useEffect(() => {
    let frame = 0;
    const track = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => app.trackOutline());
    };
    addEventListener("scroll", track, { passive: true });
    return () => {
      removeEventListener("scroll", track);
      cancelAnimationFrame(frame);
    };
  }, [app]);
}
